import { createApi } from '@reduxjs/toolkit/query/react';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse, handleUnwrapRTKError } from '@/services/utils/handlers';
import { setAppNotice } from '@/app/store/appShellSlice';

const BASE_URL = process.env.NEXT_PUBLIC_REPAROLY_API_URL ?? 'https://api.reparoly.local';

export interface ApiResponseBase<T> {
  success: boolean;
  message: string;
  data: T;
}

export type DashboardSummary = {
  repairs_completed: number; // Órdenes a estatus terminal
  clients_total: number; // Total de clientes
  open_balance: number; // Suma de (total_cost - paid_amount) en no terminales
  new_clients: number; // Clientes creados en el rango
  from: string; // ISO date-time
  to: string; // ISO date-time
};

export type DashboardSummaryParams = {
  period?: 'today' | 'week' | 'month';
  from?: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD
};

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  tagTypes: ['DashboardSummary', 'OrdersSeries', 'Technicians'],
  endpoints: (builder) => ({
    getDashboardSummary: builder.query<
      ApiResponseBase<DashboardSummary>,
      DashboardSummaryParams | void
    >({
      query: (params) => {
        const p = params ?? { period: 'today' };
        const qs = new URLSearchParams();
        if (p.period) qs.set('period', p.period);
        if (p.from) qs.set('from', p.from);
        if (p.to) qs.set('to', p.to);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        return { url: `/v1/dashboard/summary${suffix}`, method: 'GET' };
      },
      transformErrorResponse: handlerErrorResponse('Dashboard.Summary'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al obtener el resumen del dashboard',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Dashboard.Summary'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'dashboard.summary',
              endpointName: e?.endpointName || 'Dashboard.Summary',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
      providesTags: ['DashboardSummary'],
    }),
    getOrdersSeries: builder.query<
      ApiResponseBase<{ items: TimeseriesPoint[]; from: string; to: string }>,
      OrdersSeriesParams | void
    >({
      query: (params) => {
        const p = params ?? { period: 'week' };
        const qs = new URLSearchParams();
        if (p.interval) qs.set('interval', p.interval);
        if (p.period) qs.set('period', p.period);
        if (p.from) qs.set('from', p.from);
        if (p.to) qs.set('to', p.to);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        return { url: `/v1/dashboard/orders-series${suffix}`, method: 'GET' };
      },
      transformErrorResponse: handlerErrorResponse('Dashboard.OrdersSeries'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al obtener la serie de órdenes',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Dashboard.OrdersSeries'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'dashboard.orders.series',
              endpointName: e?.endpointName || 'Dashboard.OrdersSeries',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
      providesTags: ['OrdersSeries'],
    }),
    getTechnicians: builder.query<
      ApiResponseBase<{ items: ProductivityItem[]; from: string; to: string }>,
      TechniciansParams | void
    >({
      query: (params) => {
        const p = params ?? { period: 'week', strategy: 'assigned' };
        const qs = new URLSearchParams();
        if (p.strategy) qs.set('strategy', p.strategy);
        if (p.period) qs.set('period', p.period);
        if (p.from) qs.set('from', p.from);
        if (p.to) qs.set('to', p.to);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        return { url: `/v1/dashboard/technicians${suffix}`, method: 'GET' };
      },
      transformErrorResponse: handlerErrorResponse('Dashboard.Technicians'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al obtener la productividad por técnico',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Dashboard.Technicians'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'dashboard.technicians',
              endpointName: e?.endpointName || 'Dashboard.Technicians',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
      providesTags: ['Technicians'],
    }),
  }),
});
export type TimeseriesPoint = { label: string; created?: number; completed?: number; pending?: number };
export type OrdersSeriesParams = {
  interval?: 'day' | 'week' | 'month';
  period?: 'today' | 'week' | 'month';
  from?: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD
};

export type ProductivityItem = {
  user_id: number;
  display_name: string;
  completed: number;
  percent: number; // 0-100
};

export type TechniciansParams = {
  strategy?: 'assigned' | 'closer';
  period?: 'today' | 'week' | 'month';
  from?: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD
};

export const {
  useGetDashboardSummaryQuery,
  useLazyGetDashboardSummaryQuery,
  useGetOrdersSeriesQuery,
  useLazyGetOrdersSeriesQuery,
  useGetTechniciansQuery,
  useLazyGetTechniciansQuery,
} = dashboardApi;
